// « État du livre » — is the book current? (docs/PMS-LEDGER-UI-BRIEF.md §2, panel 1.)
//
// Read-only. Shows, from `fin_ledger_status`: through which day the book is posted, why the next days
// are blocked (in French; an unknown code is shown as it is), the poster's heartbeat and drift. The
// words come from fin-core, the numbers from the port: this file only arranges them.
//
// Honesty: a missing right is « Droit manquant », never an empty panel; a read that fails says so and
// offers a retry; every figure carries the time it was read.

import { useCallback, useEffect, useState } from 'react';
import {
  blockerLabel,
  bookHealth,
  driftLabel,
  formatClockParis,
  formatDay,
  formatDayLong,
  outcomeLabel,
  type LedgerReader,
  type LedgerStatus,
} from '@lemontaigu/fin-core';

// ── the pure view ───────────────────────────────────────────────────────────

export function EtatDuLivreView({ status, loadedAt }: { status: LedgerStatus; loadedAt: string }) {
  const health = bookHealth(status);
  const run = status.lastRun;
  return (
    <section className="fin-etat" data-testid="fin-etat" aria-label="État du livre">
      <div className={`fin-health fin-health--${health.level}`} role="status" data-testid="fin-health" data-level={health.level}>
        {health.reasons.map((reason) => (
          <p key={reason}>{reason}</p>
        ))}
      </div>

      <dl className="fin-facts">
        <div>
          <dt>Livre</dt>
          <dd>{status.book}, ouvert le {formatDayLong(status.openedOn)}</dd>
        </div>
        <div>
          <dt>Posté jusqu’au</dt>
          <dd data-testid="fin-posted-through">{formatDay(status.postedThrough)}</dd>
        </div>
        <div>
          <dt>Prochain jour à poster</dt>
          <dd>{status.firstUnpostedDay ? formatDay(status.firstUnpostedDay) : 'aucun : tout est posté'}</dd>
        </div>
        <div>
          <dt>Jours postés</dt>
          <dd>{status.postedDays}{status.quietDays > 0 ? ` (dont ${status.quietDays} sans activité)` : ''}</dd>
        </div>
        <div>
          <dt>Compléments (addenda)</dt>
          <dd>{status.addenda}</dd>
        </div>
      </dl>

      <h3>Poster</h3>
      <p data-testid="fin-poster">
        {run
          ? `Dernier passage à ${formatClockParis(run.finishedAt)} : ${outcomeLabel(run.outcome)}, ${run.daysPosted} jour(s) posté(s).`
          : 'Aucun passage enregistré.'}
        {status.silentMinutes !== null && ` Il y a ${status.silentMinutes} min.`}
      </p>
      {status.openRun && (
        <p className="pms-warn" data-testid="fin-open-run">
          Un passage commencé à {formatClockParis(status.openRun.startedAt)} n’a jamais fini.
        </p>
      )}

      {status.blocked.length > 0 && (
        <>
          <h3>Jours bloqués</h3>
          <div className="pms-table-wrap">
            <table className="pms-table" data-testid="fin-blocked">
              <thead>
                <tr><th>Jour</th><th>Pourquoi</th></tr>
              </thead>
              <tbody>
                {status.blocked.map((b) => (
                  <tr key={b.day}>
                    <td>{formatDay(b.day)}</td>
                    <td>{b.blockers.map(blockerLabel).join(' ; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {status.drift.length > 0 && (
        <>
          <h3>Écarts de dérive</h3>
          <p className="muted">Un jour déjà posté dont Vega dit maintenant autre chose. Rien ne se corrige tout seul.</p>
          <div className="pms-table-wrap">
            <table className="pms-table" data-testid="fin-drift">
              <thead>
                <tr><th>Jour</th><th>Nature</th><th>Source</th></tr>
              </thead>
              <tbody>
                {status.drift.map((d) => (
                  <tr key={`${d.day}|${d.kind}|${d.sourceId}`}>
                    <td>{formatDay(d.day)}</td>
                    <td>{driftLabel(d.kind)}</td>
                    <td>{d.sourceId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="muted" data-testid="fin-loaded-at">Lu à {formatClockParis(loadedAt)}.</p>
    </section>
  );
}

// ── the container: loads through the port ───────────────────────────────────

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; status: LedgerStatus; loadedAt: string }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

export function EtatDuLivre({ ledger, book }: { ledger: LedgerReader; book: string }) {
  const [state, setState] = useState<State>({ phase: 'loading' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    const r = await ledger.status(book);
    if (r.ok) setState({ phase: 'ready', status: r.value, loadedAt: new Date().toISOString() });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  }, [ledger, book]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') return <p className="muted" data-testid="fin-loading">Chargement de l’état du livre…</p>;
  if (state.phase === 'forbidden') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-forbidden">
        Droit manquant : l’état du livre est réservé à l’administrateur.
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-error">
        <p>L’état du livre est illisible : ne pas conclure que le livre est à jour. ({state.message})</p>
        <button type="button" onClick={() => void load()}>Réessayer</button>
      </div>
    );
  }
  return (
    <>
      <EtatDuLivreView status={state.status} loadedAt={state.loadedAt} />
      <button type="button" className="fin-refresh" onClick={() => void load()}>Actualiser</button>
    </>
  );
}
